import type { Locale } from '@tuic/i18n';
import {
  compositionTypeEnum,
  detailLevelEnum,
  filterProductsByService,
  findFirstCruiseService,
  getFirstAdultPrice,
  getTotalPrice,
  pagingEntityEnum,
  postCruiseList,
  ProductWithServices,
  resolveProductServices,
  sortCrieriaEnum,
  sortDirectionEnum,
} from '@tuic/css-kit';
import { cachedGetMultipleTripsDetails, Pcl } from '..';

export type PclTripWithPriceDetails = Pcl.Trip & {
  price: {
    pricePerPassenger?: number;
    priceTotal?: number;
  };
};

/**
 * Returns the cheapest trips from the cruise search & transforms them to a PCl trip with prices.
 */
export const getCheapestTrips = async (
  locale: Locale,
  numberOfTrips: number,
): Promise<PclTripWithPriceDetails[]> => {
  let products = await getCheapestTripsFromCruiseSearch(numberOfTrips);
  products = filterProductsByService(
    products,
    service => service.type === 'cruise',
  );

  let tripPrices = products
    .map(product => {
      let cruiseService = findFirstCruiseService(product);
      let tripCode = cruiseService?.journeyIdentifier;

      if (typeof tripCode === 'undefined') {
        return undefined;
      }

      return {
        tripCode,
        pricePerPassender: getFirstAdultPrice(product),
        priceTotal: getTotalPrice(product),
      };
    })
    .filter(
      (tripCode): tripCode is NonNullable<typeof tripCode> =>
        typeof tripCode !== 'undefined',
    );

  let tripCodes = tripPrices.map(data => data.tripCode);

  let trips = await cachedGetMultipleTripsDetails(tripCodes, locale);

  return trips
    .map((trip): PclTripWithPriceDetails | undefined => {
      let tripCode = trip.code ?? '';

      if (typeof tripCode === 'undefined') {
        return undefined;
      }

      let prices = tripPrices.find(
        tripPrice => tripPrice.tripCode === tripCode,
      );

      if (typeof prices === 'undefined') {
        return undefined;
      }

      return {
        price: {
          pricePerPassenger: prices.pricePerPassender,
          priceTotal: prices.priceTotal,
        },

        ...trip,
      };
    })
    .filter(
      (trip): trip is PclTripWithPriceDetails => typeof trip !== 'undefined',
    );
};
const getCheapestTripsFromCruiseSearch = async (
  numberOfTrips: number,
): Promise<ProductWithServices[]> => {
  let response = await postCruiseList({
    detailLevel: [detailLevelEnum.PRICE_DETAILS],
    sort: {
      criteria: sortCrieriaEnum.PRICE,
      direction: sortDirectionEnum.ASC,
    },
    passenger: {
      adults: 2,
      children: 0,
    },
    period: {
      start: {
        date: new Date().toISOString().split('T')[0],
        postOffset: 100,
      },
    },
    composition: compositionTypeEnum.JOURNEY_IDENTIFIER,
    paging: {
      resultsFrom: 1,
      resultsTotal: numberOfTrips,
      entity: pagingEntityEnum.JOURNEY,
    },
  });

  return resolveProductServices(response?.products, response?.services);
};
